import { useMemo, useState } from "react";
import { PageRenderer } from "@/components/sections/PageRenderer";
import { SectionTitle } from "@/components/common/SectionTitle";
import { EventCard } from "@/components/cards/EventCard";
import { RedBand } from "@/components/common/RedBand";
import { Loader, EmptyState } from "@/components/common/Loader";
import { usePublicContent } from "@/lib/useContent";
import { useSite } from "@/context/SiteContext";
import { cn } from "@/lib/utils";

const FILTERS = ["All", "Camps", "Clinics", "Evaluations", "Parent Meetings", "Team Training", "Online", "Community"];

export default function Events() { return <PageRenderer pageKey="events" />; }

export const EventsBody = () => {
  const { data, loading } = usePublicContent("events");
  const { settings } = useSite();
  const [active, setActive] = useState("All");

  const filtered = useMemo(() => {
    if (active === "All") return data;
    return data.filter((e) => e.event_type === active);
  }, [data, active]);

  return (
    <div data-testid="events-page">
      {/* hero via PageRenderer */}

      <section id="events-find" className="scroll-mt-24 bg-background py-16 sm:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <SectionTitle eyebrow="Upcoming" title="Find Your Next Event" subtitle="Filter by event type, then register in seconds." />

          <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Event types" data-testid="event-filter-tabs">
            {FILTERS.map((f) => {
              const isActive = active === f;
              return (
                <button
                  key={f}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(f)}
                  data-testid={`event-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className={cn(
                    "rounded-full border px-4 py-2 font-condensed text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 active:scale-[0.98]",
                    isActive ? "border-[hsl(var(--brand-red))] bg-brand text-white" : "border-border bg-transparent text-muted-foreground hover:border-[hsl(var(--brand-red))] hover:text-foreground"
                  )}
                >
                  {f}
                </button>
              );
            })}
          </div>

          <div className="mt-10">
            {loading ? (
              <Loader />
            ) : filtered.length === 0 ? (
              <EmptyState title="No events in this category" description="Try a different filter or check back soon." />
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((e) => <EventCard key={e.id} item={e} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      <div id="events-cta" className="scroll-mt-24">
      <RedBand
        eyebrow="Can't Find What You Need?"
        title="Talk To Our Team"
        text="Questions about an event, camp, or tryout? We're here to help."
        primary={{ label: "Contact Us", to: "/contact" }}
        secondary={{ label: "Book a Session", to: "/booking" }}
        testid="events-cta"
      />
      </div>
    </div>
  );
}
